import { Chip } from '@material-ui/core';
import { Product, StockInfo as StockInfoType } from '@setvi/shared/interfaces';
import { useSingerLabel } from 'hooksv2';

interface StockInfoProps {
  stockInfo: Product['stockInfo'];
}

/**
 * A product is considered "Non-Stocked" when it is not stocked in all warehouses
 * and none of its warehouses holds stock. This mirrors the condition behind the
 * "Not Stocked" badge in getStockInfo below - keep both in sync.
 */
export const isNonStocked = (stockInfo?: StockInfoType) => {
  if (!stockInfo) return false;

  const { allWarehouses = false, warehouses } = stockInfo;

  if (allWarehouses) return false;

  return !warehouses?.some(warehouse => warehouse.stock);
};

/**
 * Singer warehouse names carry the state after a dash, e.g. "ELVERSON-PA (001)"
 * -> "PA" (DEV-930). Names without that shape are shown as they are.
 */
const getWarehouseLabel = (warehouseName = '') =>
  warehouseName.match(/-\s*([A-Za-z]{2})\s*(?:\(|$)/)?.[1]?.toUpperCase() ||
  warehouseName.trim();

/**
 * `isSinger` switches to the DEV-930 badge: the states of every stocked
 * warehouse, or plain "Stocked" when all of them are. `warehouses` lists every
 * warehouse with its own stocked flag, not only the stocked ones.
 */
export const getStockInfo = (
  { allWarehouses = false, warehouses = [] }: Product['stockInfo'],
  isSinger = false
) => {
  const warehousesWithStock = warehouses.filter(warehouse => warehouse.stock);

  if (allWarehouses) return 'Stocked';

  if (!isSinger)
    return warehousesWithStock.length > 0
      ? `Stocked - ${warehousesWithStock[0].warehouseName}`
      : 'Not Stocked';

  if (warehouses.length > 1 && warehousesWithStock.length === warehouses.length)
    return 'Stocked';

  const labels = Array.from(
    new Set(
      warehousesWithStock
        .map(({ warehouseName }) => getWarehouseLabel(warehouseName))
        .filter(Boolean)
    )
  );

  if (labels.length > 0) return `Stocked - ${labels.join(', ')}`;
  if (warehousesWithStock.length > 0) return 'Stocked';

  return 'Not Stocked';
};

export const StockInfo = ({ stockInfo }: StockInfoProps) => {
  const { isSinger } = useSingerLabel();

  if (!stockInfo) return null;

  return (
    <Chip
      variant="outlined"
      size="small"
      label={getStockInfo(stockInfo, isSinger)}
    />
  );
};
